import { Link } from "react-router-dom";
import PublicLayout from "../../components/marketing/PublicLayout";
import { useMarketingLang } from "../../lib/useMarketingLang";
import { IMG } from "../../i18n/marketingImages";
import { Leaf, Ship, Waves, Heart, Star, ArrowRight } from "lucide-react";

const pillarIcons = [Waves, Ship, Leaf, Heart];

export default function MarketingHome() {
  const { t } = useMarketingLang();
  return (
    <PublicLayout>
      {/* HERO */}
      <section
        className="relative min-h-[560px] lg:min-h-[680px] flex items-end"
        data-testid="home-hero"
      >
        <img
          src={IMG.hero}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2B] via-[#0F1A2B]/70 to-[#0F1A2B]/30" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pb-14 lg:pb-20 pt-24 lg:pt-32 w-full">
          <div className="text-[10px] lg:text-xs uppercase tracking-[0.42em] text-[#ECC974] mb-4">
            {t.home.eyebrow}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] max-w-3xl">
            {t.home.heroTitle}
          </h1>
          <p className="text-white/85 mt-5 text-base lg:text-lg max-w-xl">
            {t.home.heroSub}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/book"
              data-testid="home-cta-book"
              className="inline-flex items-center gap-2 rounded-full bg-[#A57F39] hover:bg-[#BD984D] transition text-white uppercase tracking-widest text-xs font-bold px-6 py-3.5"
            >
              {t.home.ctaAvailability} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/flate"
              data-testid="home-cta-fleet"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 hover:bg-white/10 transition text-white uppercase tracking-widest text-xs font-bold px-6 py-3.5"
            >
              {t.home.ctaFleet}
            </Link>
          </div>
        </div>
      </section>

      {/* BOATS PICK */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 grid md:grid-cols-2 gap-6">
        <BoatCard
          img={IMG.gl33square}
          title="Greenline 33"
          desc="Greenline 33 er en stilfull og miljøvennlig elektrisk båt, perfekt for familietur eller ferie for to."
          bullets={["7 soveplasser", "Autopilot", "Front- & akterthrustere", "Solsenger på baugen", "Aircondition", "Solpaneler"]}
          to="/baater/greenline-33"
          testid="home-boat-33"
        />
        <BoatCard
          img={IMG.gl39square}
          title="Greenline 39"
          desc="Greenline 39 er en elegant elektrisk yacht som gir et raffinert interiør og en genuin premium-opplevelse ombord."
          bullets={["6 soveplasser", "Autopilot", "Front- & akterthrustere", "Pilotdør på styrbord", "Aircondition", "Solpaneler"]}
          to="/baater/greenline-39"
          testid="home-boat-39"
        />
      </section>

      {/* Pillars */}
      <section className="bg-[#F8F7F2] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F1A2B] max-w-3xl leading-tight">
            {t.home.section2Title}
          </h2>
          <p className="text-[#0F1A2B]/70 mt-4 max-w-2xl text-base lg:text-lg">
            {t.home.section2Body}
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.home.pillars.map((p, i) => {
              const Icon = pillarIcons[i % pillarIcons.length];
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-[#A57F39]/15 p-6 shadow-sm hover:shadow-lg transition"
                  data-testid={`home-pillar-${i}`}
                >
                  <div className="h-12 w-12 rounded-full bg-[#A57F39]/10 text-[#A57F39] flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F1A2B] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#0F1A2B]/70 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet intro */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39] mb-3">
            {t.home.fleetTitle}
          </div>
          <p className="text-xl lg:text-2xl text-[#0F1A2B] font-semibold leading-snug mb-5">
            {t.home.fleetIntro}
          </p>
          <p className="text-[#0F1A2B]/70 leading-relaxed">
            {t.home.fleetBody}
          </p>
          <Link
            to="/flate"
            data-testid="home-fleet-link"
            className="inline-flex items-center gap-2 mt-6 text-[#A57F39] font-semibold hover:gap-3 transition-all"
          >
            {t.nav.fleet} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="relative">
          <img
            src={IMG.bandak}
            alt=""
            className="w-full aspect-[4/3] object-cover rounded-3xl shadow-xl"
          />
          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-white rounded-2xl shadow-lg p-4 border border-[#A57F39]/20">
            <div className="flex items-center gap-1 text-[#A57F39]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#A57F39] mt-1">{t.home.excellent}</div>
            <div className="text-xs text-[#0F1A2B]/70 mt-1">{t.home.reviewsBased}</div>
          </div>
        </div>
      </section>

      {/* Tours */}
      <section className="bg-[#0F1A2B] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
            {t.home.toursTitle}
          </h2>
          <p className="text-white/70 mt-4 max-w-3xl text-base lg:text-lg">
            {t.home.toursSub}
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {t.home.tourCards.map((tour, i) => {
              const bg = [IMG.coastline2, IMG.vrangfoss, IMG.allInOne][i];
              const links = ["/opplevelser/kyst", "/opplevelser/kanal", "/opplevelser/alt-i-ett"];
              return (
                <Link
                  key={i}
                  to={links[i]}
                  data-testid={`home-tour-${i}`}
                  className="group relative overflow-hidden rounded-2xl aspect-[3/4] border border-white/10 block"
                >
                  <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2B] via-[#0F1A2B]/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974] mb-2">
                      {tour.tag}
                    </div>
                    <h3 className="text-xl font-bold leading-snug mb-3">{tour.title}</h3>
                    <ul className="space-y-1 text-sm text-white/85">
                      {tour.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="text-[#BD984D] mt-1">•</span> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function BoatCard({ img, title, desc, bullets, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block rounded-2xl overflow-hidden border border-[#A57F39]/20 bg-white shadow-sm hover:shadow-2xl transition"
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img src={img} alt={title} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
      </div>
      <div className="p-6">
        <h3 className="text-2xl font-bold text-[#0F1A2B] mb-2">{title}</h3>
        <p className="text-sm text-[#0F1A2B]/70 mb-4 leading-relaxed">{desc}</p>
        <ul className="space-y-1.5 text-sm text-[#0F1A2B]/85">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#A57F39] mt-1">✓</span> {b}
            </li>
          ))}
        </ul>
        <div className="mt-5 inline-flex items-center gap-2 text-[#A57F39] font-semibold text-sm">
          Se detaljer & booking <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
        </div>
      </div>
    </Link>
  );
}
